import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * The CGUARD wordmark — set in Neue Machina, all caps, tracked.
 *
 * No "CG" stacked-tile. No rounded-square icon over the letters. The brand
 * mark IS the typography — that's why we ship a single display weight.
 *
 * Three variants:
 *   default  — full wordmark with optional tagline
 *   compact  — wordmark only, used in tight topbars
 *   seal     — wordmark + the three-hexagon seal-glyph (SIRA → SSP → SSU)
 */
export interface WordmarkProps extends React.HTMLAttributes<HTMLAnchorElement> {
  href?: string;
  /** Show the "SIRA-LICENSED EDGE PSIM" tagline below the wordmark. */
  tagline?: string;
  /** "default" (with tagline), "compact" (mark only), "seal" (mark + glyph). */
  variant?: 'default' | 'compact' | 'seal';
  /** Force the surface color: "auto" inherits, "fg" uses --color-foreground. */
  tone?: 'auto' | 'fg';
}

export const Wordmark = React.forwardRef<HTMLAnchorElement, WordmarkProps>(
  (
    {
      href = '/',
      tagline = 'SIRA-LICENSED EDGE PSIM',
      variant = 'default',
      tone = 'fg',
      className,
      ...rest
    },
    ref,
  ) => {
    const showTagline = variant === 'default' && tagline;
    const showSeal = variant === 'seal';

    return (
      <a
        ref={ref}
        href={href}
        className={cn(
          'group inline-flex items-center gap-3 select-none no-underline outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2',
          tone === 'fg' ? 'text-[var(--color-foreground)]' : 'text-current',
          className,
        )}
        aria-label="CGuard — go to home"
        {...rest}
      >
        {showSeal && <SealGlyph aria-hidden className="size-7 shrink-0" />}
        <span className="flex flex-col leading-none">
          <span
            className="font-display font-extrabold tracking-[0.18em] text-[1.0625rem]"
            style={{ fontStretch: '100%' }}
          >
            CGUARD
          </span>
          {showTagline && (
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.22em] text-[var(--color-foreground-3)] mt-1">
              {tagline}
            </span>
          )}
        </span>
      </a>
    );
  },
);
Wordmark.displayName = 'Wordmark';

/**
 * SealGlyph — three concentric outlined hexagons stacked top-left.
 * Represents the regulatory hierarchy: SIRA → SSP → SSU.
 */
export const SealGlyph = React.forwardRef<SVGSVGElement, React.SVGAttributes<SVGSVGElement>>(
  ({ className, ...rest }, ref) => (
    <svg
      ref={ref}
      viewBox="0 0 28 28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
      className={className}
      {...rest}
    >
      {/* Outer hex — SIRA */}
      <polygon points="14,2 24,7.5 24,20.5 14,26 4,20.5 4,7.5" />
      {/* Mid hex — SSP */}
      <polygon points="14,6 21,9.75 21,18.25 14,22 7,18.25 7,9.75" opacity="0.55" />
      {/* Inner hex — SSU (filled with currentColor at low opacity) */}
      <polygon
        points="14,10 18,12.25 18,15.75 14,18 10,15.75 10,12.25"
        fill="currentColor"
        fillOpacity="0.85"
        stroke="none"
      />
    </svg>
  ),
);
SealGlyph.displayName = 'SealGlyph';
